import React from "react";
import { TextWrapper, TextWrapperSpan, Wrapper, StyledImage, CaseNumberTextLink } from "../../constants/common/StyledGlobalConstants";
import BlueVectorIcon from "../../assets/icons/Blue_Vector.svg";
import { Line } from "@vds/lines";
import GetReadyForAppointment from "../../assets/icons/booking_confirmed.svg";
import { UnorderedList, ListItem } from "@vds/lists";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { getPendoId } from "../../util/cfstPendoUtils";
import { DEVICE_BREAKPOINTS } from "../../constants/appConstants";

const { TABLET, DESKTOP } = DEVICE_BREAKPOINTS;

const StyledListItem = styled(ListItem)`
  font-family: "neue-haas-grotesk-text";
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  padding: 0;
`;

const StyledTextLink = styled(CaseNumberTextLink)`
  color: #006fc1 !important;
  text-decoration-line: underline;
  border-bottom: none;
  &:hover {
    text-decoration-thickness: 2px;
    border-bottom: none;
  }
`;

const BookingConfirmed = () => {
  const { windowSize, emailAddress: loggedInEmail } = useSelector((state) => state.ACCOUNT);
  const { selectedSlotDetails, jobResponseData, isReschedule, jobDetails, isUpdateCaseApiFailed, contactFormValues, rescheduleSchedulingEmail } = useSelector(
    (state) => state.TECHNICIANAPPOINTMENT
  );
  const { slotDate, startTime, vendorName } = selectedSlotDetails;
  const updatedRevealEmail = isReschedule
    ? rescheduleSchedulingEmail
    : contactFormValues?.existingContact?.email || loggedInEmail;

  const pocEmail = isReschedule ? jobDetails.email : jobResponseData.email,
    emailSentTo = (
      <>
        <b>{updatedRevealEmail}</b>
        {updatedRevealEmail !== pocEmail && (
          <>
            {" and "}
            <b>{pocEmail}</b>
          </>
        )}
      </>
    );
  const appointmentNumber = isReschedule ? jobDetails.id : jobResponseData.id;

  return (
    <Wrapper display="flex" margin="0.5rem 0 6rem 0" gap="3rem">
      <Wrapper display="flex" flexDirection="column" gap={windowSize.width >= DESKTOP ? "32px" : "24px"}>
        {windowSize.width > 767 ? (
          <>
            <h1 className="sr-only" aria-live="assertive" role="alert">
              Booking confirmed
            </h1>
            <TextWrapper
              aria-label="Booking confirmed"
              fontFamily="neue-haas-grotesk-display"
              fontSize="2.5rem"
              fontWeight="700"
              lineHeight="3rem"
              tabIndex="-1"
              aria-hidden="true"
            >
              Booking confirmed
            </TextWrapper>
          </>
        ) : (
          <TextWrapper
            aria-label="Booking confirmed"
            fontFamily="neue-haas-grotesk-display"
            fontSize="2.5rem"
            fontWeight="700"
            lineHeight="3rem"
            role="alert"
          >
            Booking confirmed
          </TextWrapper>
        )}
        {!isUpdateCaseApiFailed && (
          <TextWrapper
            aria-label="We’ll email a confirmation to the address below."
            fontFamily="neue-haas-grotesk-text"
            fontSize="1rem"
            fontWeight="700"
            lineHeight="1.375rem"
          >
            We’ll email a confirmation to the address below.
          </TextWrapper>
        )}
      </Wrapper>
      <Wrapper display="flex" gap={"16px"} flexDirection="column">
        <TextWrapper
          aria-label="Confirmation of appointment details"
          fontFamily="neue-haas-grotesk-text"
          fontSize="1.25rem"
          fontWeight="700"
          lineHeight="1.5rem"
        >
          Confirmation of appointment details
        </TextWrapper>
        <Wrapper display="flex" gap="8px" fontFamily="neue-haas-grotesk-text" fontSize="0.875rem" lineHeight="1.25rem" flexDirection="column">
          {!isUpdateCaseApiFailed && (
            <TextWrapper fontWeight="400">
              Email sent to <TextWrapperSpan>{emailSentTo}</TextWrapperSpan>
            </TextWrapper>
          )}
          <Wrapper display="grid" gap="8px" gridTemplateColumns={windowSize.width >= TABLET ? "auto 1fr" : "100%"}>
            <TextWrapper fontWeight="700">Appointment date and time:</TextWrapper>
            <TextWrapper fontWeight="400">{`${slotDate} at ${startTime}`}</TextWrapper>
          </Wrapper>
          <Wrapper display="grid" gap="8px" gridTemplateColumns={windowSize.width >= TABLET ? "auto 1fr" : "100%"}>
            <TextWrapper fontWeight="700">Appointment number:</TextWrapper>
            <TextWrapper fontWeight="400">{appointmentNumber}</TextWrapper>
          </Wrapper>
          <Wrapper display="grid" gap="8px" gridTemplateColumns={windowSize.width >= TABLET ? "auto 1fr" : "100%"}>
            <TextWrapper fontWeight="700">Technician:</TextWrapper>
            <TextWrapper fontWeight="400">{vendorName}</TextWrapper>
          </Wrapper>
        </Wrapper>
        <Wrapper display="flex" gap="32px">
          <TextWrapper
            fontFamily="neue-haas-grotesk-text"
            fontSize="0.875rem"
            lineHeight="1.25rem"
            fontWeight="400"
            margin={windowSize.width >= TABLET && "8px 0 0 0"}
          >
            The technician will call the point of contact before the appointment to confirm the details.
          </TextWrapper>
          <Line type="xLight" inverted={false} />
          <Wrapper display="flex" gap="24px">
            <TextWrapper fontFamily="neue-haas-grotesk-text" fontSize="1.5rem" lineHeight="2rem" fontWeight="700">
              Get ready for the appointment
            </TextWrapper>
            <Wrapper padding="24px" border="1px solid #D8DADA" borderRadius="8px" width="100%">
              <Wrapper display="grid" gridTemplateColumns={windowSize.width >= TABLET ? "90px 1fr" : "100%"} gap="24px">
                <StyledImage width="90px" height="90px" src={GetReadyForAppointment} alt="img" aria-hidden="true" />
                <Wrapper display="flex" gap="16px">
                  <UnorderedList>
                    <StyledListItem>Ensure that the point of contact, vehicles and keys are available at the selected date and time</StyledListItem>
                    <StyledListItem>Make sure your vehicles are accessible and clutter free</StyledListItem>
                    <StyledListItem>
                      Make a plan for any removed devices. If you don’t intend to transfer them into another vehicle or asset, read the{" "}
                      <StyledTextLink
                        id={getPendoId("contracts-and-cancellations-faq-link")}
                        tabIndex={0}
                        aria-label="Contracts and cancellations FAQ link"
                        onClick={() => window.open("https://reveal-help.verizonconnect.com/hc/articles/17190169888787")}
                      >
                        Contracts and cancellations FAQ.
                      </StyledTextLink>{" "}
                      <a href="https://reveal-help.verizonconnect.com/hc/articles/17190169888787" tabIndex={-1} aria-hidden="true" target="_blank">
                        <StyledImage
                          width="12px"
                          height="12px"
                          src={BlueVectorIcon}
                          alt="link"
                          id={getPendoId("contracts-and-cancellations-faq-link-icon")}
                          tabIndex={-1}
                          aria-hidden="true"
                        />
                      </a>
                    </StyledListItem>
                  </UnorderedList>
                </Wrapper>
              </Wrapper>
            </Wrapper>
          </Wrapper>
        </Wrapper>
      </Wrapper>
    </Wrapper>
  );
};

export default BookingConfirmed;
